'use client';

// import { DesktopTable } from 'components/NewComparison/DesktopTable';
import { useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { MobileTable } from './MobileTable';
import { CustomImage as Image } from 'components/shared/custom-image';
import { DesktopTableNew } from './DesktopTableNew';
import { useTranslations } from 'next-intl';
import { ComparisonProps } from './types';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getCurrencySymbolFromEnum } from 'lib/region/countryUtils';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';
import {
  RingPriceData,
  AirRingColors,
} from 'content/products/ring/productMap/constants';
import { getCompetitorComparisonCopy } from 'content/products/ring/competitorComparisonPrices';
import styles from './index.module.css';

export const NewComparison = ({
  className,
  htmlId,
}: {
  className?: string;
  htmlId?: string;
}) => {
    const t = useTranslations('ring');
    const GlobalUI = useGlobalUI();
    const { store, currency } = useCartStore(
      useShallow((s) => ({ store: s.store, currency: s.currency })),
    );
    const uhRingPrice = useMemo(() => {
      const s = store ?? ShopifyStore.ROW;
      const c = (currency?.toLowerCase() as Currency) ?? Currency.usd;
      const symbol = getCurrencySymbolFromEnum(c);
      const price =
        RingPriceData[s]?.[AirRingColors.RAW_TITANIUM]?.price ?? 349;
      return `${symbol}${formatPriceWithComma(price, c)}`;
    }, [store, currency]);
    const competitorPrices = useMemo(() => {
      const copy = getCompetitorComparisonCopy(store, currency);
      return {
        garmin: copy.garmin,
        appleWatch: copy.appleWatch,
        oura: t('ring.comparison.ouraRing.pricing', {
          starting: copy.ouraStarting,
          subscription: copy.ouraSubscription,
        }),
      };
    }, [store, currency, t]);
    // const [isMobile, setIsMobile] = useState(false);
    const [comparatorsOrder, setComparatorsOrder] = useState<number[]>([
      0, 1, 2,
    ]);
    const [selectedActivityTrackerId, setSelectedActivityTrackerId] =
      useState<number>(0);

    const gsapContext = useGsapContext();
    const tableContainer = useRef<HTMLTableElement>(null);

    // Generate table summary for screen readers
    const generateTableSummary = () => {
      const currentComparators = comparatorsOrder.slice(0, -1).map((id) => {
        const item = ComparisonItems[id];
        return typeof item.title === 'string' ? item.title : 'Product';
      });

      return `Comparison table showing features of Ultrahuman Ring AIR compared to ${currentComparators.join(
        ', ',
      )}. Features include form factor, weight, real-time metrics, sleep tracking, movement tracking, heart rate monitoring, HRV monitoring, skin temperature tracking, battery life, and other health monitoring capabilities.`;
    };

    const ComparisonItems = [
      {
        // title: 'Garmin Smartwatch',
        title: t('ring.comparison.garminWatch.title'),
        id: 0,
        // formFactor: 'Wrist-worn',
        formFactor: t('ring.comparison.garminWatch.form.formFactor.a'),
        // formFactorDisplay: 'with display',
        formFactorDisplay: t('ring.comparison.garminWatch.form.formFactor.b'),
        formFactorIcon: 'web_v2/ring-buy/comparison/formFactor_watch.svg',
        weight: t('ring.comparison.garminWatch.form.weight.bold'),
        weightIcon: 'web_v2/ring-buy/comparison/garmin_weight.svg',
        weightSub: t('ring.comparison.garminWatch.form.weight.sub'),
        weightThin: t('ring.comparison.garminWatch.form.weight.thin'),
        sleepTracking: true,
        sleepMetrics: t('ring.comparison.garminWatch.sleep.sleepMetrics'),
        movementAndWorkoutTracking: true,
        movementAndWorkoutTrackingParams: '(steps, workouts)',
        stepCounting: true,
        distance: true,
        calories: true,
        heartRateMonitoring: t(
          'ring.comparison.garminWatch.realTimeMetrics.hrMonitoring.bold',
        ),
        heartRateMonitoringSub: t(
          'ring.comparison.garminWatch.realTimeMetrics.hrMonitoring.thin',
        ),
        vo2Max: true,
        gps: true,
        hrvMonitoringRtm: t(
          'ring.comparison.garminWatch.realTimeMetrics.hrvMonitoring.bold',
        ),
        hrvMonitoringRtmSub: t(
          'ring.comparison.garminWatch.realTimeMetrics.hrvMonitoring.thin',
        ),
        hrvMonitoring: 'Static and non-actionable',
        hrvMonitoringSub: 'throughout the day',
        hrvMetrics: t('ring.comparison.garminWatch.recovery.hrvMetrics'),
        skinTempTracking: t(
          'ring.comparison.garminWatch.realTimeMetrics.skinTemp.bold',
        ),
        skinTempTrackingSub: t(
          'ring.comparison.garminWatch.realTimeMetrics.skinTemp.thin',
        ),
        temperatureTracking: true,
        temperatureTrackingSub: '(varies by model)',
        stressMonitoring: true,
        spo2Monitoring: true,
        respiratoryRate: true,
        batteryLife: t('ring.comparison.garminWatch.function.batteryLife.bold'),
        batteryLifeIcon: 'web_v2/ring-buy/comparison/garmin_battery.svg',
        batteryLifeSub: '(varies by model)',
        fullChargingTime: t(
          'ring.comparison.garminWatch.function.fullChargingTime.bold',
        ),
        fullChargingSub: t(
          'ring.comparison.garminWatch.function.fullChargingTime.thin',
        ),
        waterproof: t('ring.comparison.garminWatch.function.waterProof.bold'),
        airplaneMode: true,
        findMyDevice: true,
        goalTracking: true,
        realTimeNotifications: true,
        circadianRhythmTracking: false,
        caffeineWindowDetection: false,
        afibDetection: false,
        subscriptionRequired: t('ring.comparison.feature.subscription.notReq'),
        performanceCoach: false,
        tempBasedOvulation: false,
        priceRange: competitorPrices.garmin,
      },
      // {
      //   title: t('ring.comparison.whoopStrap.title'),
      //   id: 1,
      //   formFactor: (
      //     t('ring.comparison.whoopStrap.form.formFactor.a')
      //   ),
      //   formFactorDisplay: (
      //     t('ring.comparison.whoopStrap.form.formFactor.b')
      //   ),
      //   weightIcon: 'web_v2/ring-buy/comparison/weight.svg',
      //   weight: (
      //     t('ring.comparison.whoopStrap.form.weight.bold')
      //   ),
      //   formFactorIcon: 'web_v2/ring-buy/comparison/formFactor_band.svg',
      //   weightSub: '',
      //   weightThin: (
      //     t('ring.comparison.whoopStrap.form.weight.thin')
      //   ),
      //   sleepTracking: true,
      //   sleepMetrics: (
      //     t('ring.comparison.whoopStrap.sleep.sleepMetrics')
      //   ),
      //   movementAndWorkoutTracking: true,
      //   movementAndWorkoutTrackingParams: '(steps, strain)',
      //   stepCounting: false,
      //   distance: true,
      //   calories: true,
      //   heartRateMonitoring: (
      //     t('ring.comparison.whoopStrap.realTimeMetrics.hrMonitoring.bold')
      //   ),
      //   heartRateMonitoringSub: (
      //     t('ring.comparison.whoopStrap.realTimeMetrics.hrMonitoring.thin')
      //   ),
      //   vo2Max: false,
      //   gps: false,
      //   hrvMonitoringRtm: (
      //     t('ring.comparison.whoopStrap.realTimeMetrics.hrvMonitoring.bold')
      //   ),
      //   hrvMonitoringRtmSub: '',
      //   hrvMonitoring: 'Static and non-actionable',
      //   hrvMonitoringSub: 'comprehensive and continuous monitoring',
      //   hrvMetrics: (
      //     t('ring.comparison.whoopStrap.recovery.hrvMetrics')
      //   ),
      //   skinTempTracking: (
      //     t('ring.comparison.whoopStrap.realTimeMetrics.skinTemp.bold')
      //   ),
      //   skinTempTrackingSub: '',
      //   temperatureTracking: true,
      //   temperatureTrackingSub: '',
      //   stressMonitoring: true,
      //   spo2Monitoring: true,
      //   respiratoryRate: true,
      //   batteryLife: (
      //     t('ring.comparison.whoopStrap.function.batteryLife.bold')
      //   ),
      //   batteryLifeIcon: 'web_v2/ring-buy/comparison/whoop_battery.svg',
      //   batteryLifeSub: '',
      //   fullChargingTime: (
      //     t('ring.comparison.whoopStrap.function.fullChargingTime.bold')
      //   ),
      //   fullChargingSub: '',
      //   waterproof: (
      //     t('ring.comparison.whoopStrap.function.waterProof.bold')
      //   ),
      //   airplaneMode: false,
      //   findMyDevice: false,
      //   goalTracking: true,
      //   realTimeNotifications: false,
      //   circadianRhythmTracking: false,
      //   caffeineWindowDetection: false,
      //   afibDetection: false,
      //   subscriptionRequired: (
      //     t('ring.comparison.feature.subscription.req')
      //   ),
      //   performanceCoach: false,
      //   tempBasedOvulation: false,
      //   priceRange: (
      //     t('ring.comparison.whoopStrap.pricing')
      //   ),
      // },
      {
        title: t('ring.comparison.ouraRing.title'),
        id: 2,
        formFactor: t('ring.comparison.ouraRing.form.formFactor.a'),
        formFactorDisplay: t('ring.comparison.ouraRing.form.formFactor.b'),
        formFactorIcon: 'web_v2/ring-buy/comparison/formFator_ring.svg',
        weight: t('ring.comparison.ouraRing.form.weight.bold'),
        weightIcon: 'web_v2/ring-buy/comparison/pennies.svg',
        weightSub: t('ring.comparison.ouraRing.form.weight.sub'),
        weightThin: t('ring.comparison.ouraRing.form.weight.thin'),
        sleepTracking: true,
        sleepMetrics: t('ring.comparison.ouraRing.sleep.sleepMetrics'),
        movementAndWorkoutTracking: true,
        movementAndWorkoutTrackingParams: '(steps, workouts)',
        stepCounting: true,
        distance: true,
        calories: true,
        heartRateMonitoring: t(
          'ring.comparison.ouraRing.realTimeMetrics.hrMonitoring.bold',
        ),
        heartRateMonitoringSub: t(
          'ring.comparison.ouraRing.realTimeMetrics.hrMonitoring.thin',
        ),
        vo2Max: true,
        gps: false,
        hrvMonitoringRtm: t(
          'ring.comparison.ouraRing.realTimeMetrics.hrvMonitoring.bold',
        ),
        hrvMonitoringRtmSub: '',
        hrvMonitoring: 'Yes',
        hrvMonitoringSub: '',
        hrvMetrics: t('ring.comparison.ouraRing.recovery.hrvMetrics'),
        skinTempTracking: t(
          'ring.comparison.ouraRing.realTimeMetrics.skinTemp.bold',
        ),
        skinTempTrackingSub: t(
          'ring.comparison.ouraRing.realTimeMetrics.skinTemp.thin',
        ),
        temperatureTracking: true,
        temperatureTrackingSub: '',
        stressMonitoring: true,
        spo2Monitoring: true,
        respiratoryRate: true,
        batteryLifeIcon: 'web_v2/ring-buy/comparison/uh_battery.svg',
        batteryLife: t('ring.comparison.ouraRing.function.batteryLife.bold'),
        batteryLifeSub: '',
        fullChargingTime: t(
          'ring.comparison.ouraRing.function.fullChargingTime.bold',
        ),
        fullChargingSub: '',
        waterproof: t('ring.comparison.ouraRing.function.waterProof.bold'),
        airplaneMode: true,
        findMyDevice: true,
        goalTracking: true,
        realTimeNotifications: false,
        circadianRhythmTracking: true,
        caffeineWindowDetection: false,
        afibDetection: false,
        subscriptionRequired: t('ring.comparison.feature.subscription.req'),
        performanceCoach: false,
        tempBasedOvulation: true,
        priceRange: competitorPrices.oura,
      },
      {
        title: t('ring.comparison.appleWatch.title'),
        id: 3,
        formFactor: t('ring.comparison.appleWatch.form.formFactor.a'),
        formFactorDisplay: t('ring.comparison.appleWatch.form.formFactor.b'),
        formFactorIcon: 'web_v2/ring-buy/comparison/formFactor_watch.svg',
        weight: t('ring.comparison.appleWatch.form.weight.bold'),
        weightIcon: 'web_v2/ring-buy/comparison/apple_weight.svg',
        weightSub: '',
        weightThin: t('ring.comparison.appleWatch.form.weight.thin'),
        sleepTracking: true,
        sleepMetrics: t('ring.comparison.appleWatch.sleep.sleepMetrics'),
        movementAndWorkoutTracking: true,
        movementAndWorkoutTrackingParams: '(steps, workouts)',
        stepCounting: true,
        distance: true,
        calories: true,
        heartRateMonitoring: t(
          'ring.comparison.appleWatch.realTimeMetrics.hrMonitoring.bold',
        ),
        heartRateMonitoringSub: t(
          'ring.comparison.appleWatch.realTimeMetrics.hrMonitoring.thin',
        ),
        vo2Max: true,
        gps: true,
        hrvMonitoringRtm: t(
          'ring.comparison.appleWatch.realTimeMetrics.hrvMonitoring.bold',
        ),
        hrvMonitoringRtmSub: t(
          'ring.comparison.appleWatch.realTimeMetrics.hrvMonitoring.thin',
        ),
        hrvMonitoring: 'Static and non-actionable',
        hrvMonitoringSub: 'throughout the day',
        hrvMetrics: t('ring.comparison.appleWatch.recovery.hrvMetrics'),
        skinTempTracking: t(
          'ring.comparison.appleWatch.realTimeMetrics.skinTemp.bold',
        ),
        skinTempTrackingSub: t(
          'ring.comparison.appleWatch.realTimeMetrics.skinTemp.thin',
        ),
        temperatureTracking: true,
        temperatureTrackingSub: '',
        stressMonitoring: true,
        spo2Monitoring: true,
        respiratoryRate: true,
        batteryLife: t('ring.comparison.appleWatch.function.batteryLife.bold'),
        batteryLifeIcon: 'web_v2/ring-buy/comparison/apple_battery.svg',
        batteryLifeSub: '',
        fullChargingTime: t(
          'ring.comparison.appleWatch.function.fullChargingTime.bold',
        ),
        fullChargingSub: '',
        waterproof: t('ring.comparison.appleWatch.function.waterProof.bold'),
        airplaneMode: true,
        findMyDevice: true,
        goalTracking: true,
        realTimeNotifications: true,
        circadianRhythmTracking: false,
        caffeineWindowDetection: false,
        afibDetection: true,
        subscriptionRequired: t('ring.comparison.feature.subscription.notReq'),
        performanceCoach: false,
        tempBasedOvulation: false,
        priceRange: competitorPrices.appleWatch,
      },

      // {
      //   title: 'Samsung Ring',
      //   id: 3,
      //   formFactor: 'Ring with concave design, Titanium',
      //   formFactorDisplay: 'no display',
      //   weight: '2.3 to 3 grams',
      //   weightIcon: 'web_v2/ring-buy/comparison/uh_weight.svg',
      //   weightSub: '(varies by size)',
      //   weightThin: '',
      //   sleepTracking: true,
      //   sleepMetrics: 'Basic sleep tracking',
      //   movementAndWorkoutTracking: true,
      //   movementAndWorkoutTrackingParams: '(steps, workouts)',
      //   stepCounting: true,
      //   distance: true,
      //   calories: true,
      //   heartRateMonitoring: 'Periodically',
      //   heartRateMonitoringSub: 'throughout the day',
      //   vo2Max: true,
      //   gps: true,
      //   hrvMonitoringRtm: 'Periodically',
      //   hrvMonitoringRtmSub: 'throughout the day',
      //   hrvMonitoring: 'Static and non-actionable',
      //   hrvMonitoringSub: 'throughout the day',
      //   hrvMetrics: 'Time-domain metrics (SDNN, RMSSD)',
      //   skinTempTracking: 'Limited',
      //   skinTempTrackingSub: 'nocturnal monitoring',
      //   temperatureTracking: true,
      //   temperatureTrackingSub: '',
      //   stressMonitoring: true,
      //   spo2Monitoring: true,
      //   respiratoryRate: true,
      //   batteryLife: '1-2 days',
      //   batteryLifeSub: '',
      //   fullChargingTime: '2.5 hours',
      //   fullChargingSub: '',
      //   waterproof: 'Up to 100 meters',
      //   airplaneMode: true,
      //   findMyDevice: true,
      //   goalTracking: true,
      //   realTimeNotifications: true,
      //   circadianRhythmTracking: false,
      //   caffeineWindowDetection: false,
      //   afibDetection: true,
      //   subscriptionRequired: false,
      //   priceRange: '$399-$799+',
      // },
    ] satisfies Array<ComparisonProps>;

    function changeComparisonItems() {
      const comparisonOrder = [...comparatorsOrder];

      const rotateArray = function rotateArray(
        arr: number[],
        rotateBy: number,
      ) {
        const n = arr.length;
        rotateBy %= n;

        return setComparatorsOrder(
          arr.slice(rotateBy).concat(arr.slice(0, rotateBy)),
        );
      };

      rotateArray(comparisonOrder, 1);
    }

    const animationInit = () => {
      if (!tableContainer.current || !window) return;

      gsapContext.add(() => {
        const tl = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.easeInOut',
          },
        });

        tl.set(`.th`, {
          xPercent: 75,
        });

        tl.set(`.th`, {
          xPercent: 75,
        });

        tl.fromTo(
          '.th',
          {
            xPercent: 75,
          },
          {
            xPercent: 0,
            duration: 0.35,
            stagger: 0.1,
          },
        );
      });
    };

    useEffect(() => {
      if (comparatorsOrder?.length > 3) {
        animationInit();
      }
    }, [comparatorsOrder]);

    return (
      <div
        className={[styles.root, 'u-grid', className].filter(Boolean).join(' ')}
        id={htmlId}
      >
        <div className="sticky-wrapper">
          <h3>
            {/* How we compare */}
            {t('ring.comparison.head')}
          </h3>
          <p className="subHead">
            {/* See how the Ultrahuman Ring AIR compares with Apple, Whoop, Garmin
            and the Oura ring */}
            {t('ring.comparison.subHead')}
          </p>

          {/* Hidden summary for screen readers */}
          <div
            id="comparison-table-summary"
            className="sr-only"
            aria-hidden="true"
          >
            {generateTableSummary()}
          </div>

          {/* Desktop and mobile tables both render; CSS toggles at 768px so
              SSR HTML is right at every width (useWindowSize is 0 on SSR). */}
          <div className="comparison-desktop">
              <div
                className="table-header-wrapper"
                style={{
                  position: 'sticky',
                  top: GlobalUI.headerVisible
                    ? `${Number(GlobalUI.headerHeight) + 51}px`
                    : '51px',
                  marginBottom: '2.5rem',
                  paddingBottom: '1.4rem',
                }}
              >
                <table
                  className="thead desktop"
                  ref={tableContainer}
                  aria-describedby="comparison-table-summary"
                >
                  <thead>
                    <tr>
                      <th className="property property-head" scope="col">
                        <span className="bold-text"></span>
                      </th>
                      <th
                        scope="col"
                        style={{ borderRight: '1px solid rgba(0,0,0,0.15)' }}
                      >
                        <div className="content product">
                          <span className="bold-text">
                            {/* Ultrahuman Ring */}
                            {t('ring.comparison.uhRing.title')}
                          </span>
                        </div>
                      </th>
                      {ComparisonItems && comparatorsOrder
                        ? comparatorsOrder.map((id, idx) => {
                            if (idx !== comparatorsOrder.length - 1) {
                              return (
                                <th key={idx} className="th" scope="col">
                                  <div className="content ">
                                    <span className="bold-text">
                                      {ComparisonItems[id]?.title}
                                    </span>
                                  </div>
                                </th>
                              );
                            }
                          })
                        : null}
                    </tr>
                  </thead>
                </table>
                {/* Caret button positioned absolutely relative to wrapper */}
                <button
                  className="caret-button"
                  onClick={changeComparisonItems}
                  aria-label={t(
                    'newComparison.button.ariaLabel.rotateComparisonProducts',
                  )}
                  tabIndex={0}
                >
                  <Image
                    src="web_v2/ring-buy/comparison/sliderArrowRight.png"
                    width={24}
                    height={24}
                    alt={t(
                      'newComparison.image.alt.rotateComparisonProducts',
                    )}
                    className="arrow-icon"
                  />
                </button>
              </div>
            <DesktopTableNew
              data={ComparisonItems}
              comparatorsOrder={comparatorsOrder}
              uhRingPrice={uhRingPrice}
            />
          </div>
          <div className="comparison-mobile">
            <table
              className="thead"
              style={{
                position: 'sticky',
                top: GlobalUI.headerVisible
                  ? `${Number(GlobalUI.headerHeight) + 51}px`
                  : '51px',
              }}
              aria-describedby="comparison-table-summary"
            >
              <thead>
                <tr>
                  <th className="title" scope="col">
                    <div className="title-content">
                      <span className="bold">
                        {/* Ultrahuman Ring */}
                        {t('ring.comparison.uhRing.title')}
                      </span>
                    </div>
                  </th>
                  <th className="title" scope="col">
                    <div className="title-content">
                      <select
                        name="activity-tracker"
                        className="bold"
                        id="activity-tracker"
                        onChange={(e) =>
                          setSelectedActivityTrackerId(Number(e.target.value))
                        }
                        aria-label={t(
                          'newComparison.content.ariaLabel.selectComparisonProduct',
                        )}
                      >
                        {ComparisonItems && comparatorsOrder
                          ? comparatorsOrder.map((id, idx) => {
                              return (
                                <option key={idx} value={id}>
                                  {ComparisonItems[id]?.title}
                                </option>
                              );
                            })
                          : null}
                      </select>
                    </div>
                  </th>
                </tr>
              </thead>
            </table>
            <MobileTable
              data={ComparisonItems}
              selectedActivityTrackerId={selectedActivityTrackerId}
              uhRingPrice={uhRingPrice}
            />
          </div>
        </div>
      </div>
    );
};
